/**
 * Browser (OS-level) notifications for the CRM bell.
 *
 * Deliberately foreground-only: these fire from the existing 60s poll while a
 * CRM tab is open. Nothing arrives with the browser closed — that needs Web Push
 * (service worker + VAPID), which is a separate piece of work.
 *
 * Two rules worth keeping:
 *  - Permission is requested from a user gesture, never on page load. Browsers
 *    penalise unsolicited prompts, and a denial is permanent until the user digs
 *    into site settings.
 *  - The highest notification id already shown is persisted, so a refresh or a
 *    second tab does not re-toast things the user has seen.
 */

const SEEN_KEY = "notif_last_seen_id";
const ENABLED_KEY = "notif_browser_enabled";

export const isSupported = () =>
    typeof window !== "undefined" && "Notification" in window;

export const permission = () =>
    (isSupported() ? Notification.permission : "unsupported");

/** User has both granted permission and left the toggle on. */
export const isEnabled = () =>
    isSupported() &&
    Notification.permission === "granted" &&
    localStorage.getItem(ENABLED_KEY) !== "off";

export const setEnabled = (on) =>
    localStorage.setItem(ENABLED_KEY, on ? "on" : "off");

/** Ask the browser for permission. Must be called from a click handler. */
export const requestPermission = async () => {
    if (!isSupported()) return "unsupported";
    if (Notification.permission === "granted") { setEnabled(true); return "granted"; }
    if (Notification.permission === "denied") return "denied";
    const result = await Notification.requestPermission();
    if (result === "granted") setEnabled(true);
    return result;
};

export const getLastSeenId = () => {
    const raw = localStorage.getItem(SEEN_KEY);
    const n = parseInt(raw, 10);
    return Number.isFinite(n) ? n : 0;
};

export const setLastSeenId = (id) => {
    const n = parseInt(id, 10);
    if (Number.isFinite(n) && n > getLastSeenId()) {
        localStorage.setItem(SEEN_KEY, String(n));
    }
};

/**
 * Fire a dummy notification to prove the setup works.
 *
 * Nothing is written to the database and no request is made — this is the
 * browser talking to itself. Unlike real alerts it deliberately ignores the
 * "skip while the tab is focused" rule, because someone clicking a test button
 * is looking at the tab and needs to actually see the result.
 */
export const showTestNotification = (name) => {
    if (!isSupported()) return "unsupported";
    if (Notification.permission !== "granted") return "not-granted";

    const who = name ? `Hai ${name}` : "Hai there";
    const n = new Notification(`${who} — test alert`, {
        body: "Desktop alerts are working. Real ones look like this:\n"
            + "\"Demo missed: Gowtham — the demo on 18 Jun passed with no outcome.\"",
        tag: "crm-test",
        renotify: true,
    });
    n.onclick = () => { try { window.focus(); } catch (_) {} n.close(); };
    return "sent";
};

/**
 * Show notifications for `items` (newest first, as the API returns them).
 *
 * A backlog becomes a single summary toast rather than a stack of five — waking
 * up to eleven separate toasts is how people turn the feature off.
 *
 * onOpen(item) is called when a toast is clicked; item is null for the summary.
 */
export const showNotifications = (items, newCount, onOpen) => {
    if (!isEnabled() || !items || !items.length) return;

    // Don't toast while the user is already looking at the tab — the bell badge
    // is enough, and an OS toast for something on screen is just noise.
    if (typeof document !== "undefined" && document.visibilityState === "visible") return;

    const open = (item) => {
        try { window.focus(); } catch (_) {}
        if (onOpen) onOpen(item);
    };

    if (newCount > 3) {
        const n = new Notification(`${newCount} new CRM alerts`, {
            body: items.slice(0, 3).map(i => i.title).join("\n"),
            tag: "crm-summary",           // replaces its own previous toast
            renotify: true,
        });
        n.onclick = () => { open(null); n.close(); };
        return;
    }

    items.forEach(item => {
        const n = new Notification(item.title || "CRM alert", {
            body: (item.message || "").slice(0, 180),
            tag: `crm-notif-${item.id}`,  // never double-toast the same row
        });
        n.onclick = () => { open(item); n.close(); };
    });
};
